import PropTypes from 'prop-types';
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Container,
    Form,
    Row,
    Spinner,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import React, { useMemo, useRef, useState } from 'react';

import SEO from '@/components/common/SEO';
import InfoTooltip from '@/components/common/InfoTooltip';
import PreviewBanner from '@/components/common/PreviewBanner';
import useDataSources from '@/shared/hooks/useDataSources';
import { deriveKeywordFields } from '@/shared/utils/normalizeDataSource';
import { COLORADO_REGIONS } from '@/shared/utils/coloradoRegions';

import { buildQueryPayload, describeQueryPayload } from './buildQueryPayload';
import './AdvancedQuery.css';

// Pillar display order + solid badge variant, matching the site's existing
// pillar convention and the app-wide solid-badge style. Dark fills (success
// green, info dark-blue) use
// white text; light fills (warning gold, secondary sky) take `text="dark"` —
// the same rule the rest of the app applies to those colors.
const PILLARS = [
    { key: 'Environmental', variant: 'success' },
    { key: 'Social', variant: 'info' },
    { key: 'Economic', variant: 'warning', text: 'dark' },
    { key: 'Other', variant: 'secondary', text: 'dark' },
];

const OPERATORS = [
    { value: 'and', label: 'AND' },
    { value: 'or', label: 'OR' },
    { value: 'not', label: 'NOT' },
];

const TIME_OPTIONS = [
    { value: 'latest', label: 'Latest available' },
    { value: 'custom', label: 'Custom date range' },
];

// Suggested-term chips beyond this count are hidden behind a "show all" toggle
// so a source with many tags does not flood the step.
const SUGGESTION_CAP = 10;

// ---------------------------------------------------------------------------
// Step 1 — Data source selection (grouped by sustainability pillar)
// ---------------------------------------------------------------------------
function SourceSelector({ sources, selectedIds, onToggle, loading, error }) {
    const [filter, setFilter] = useState('');
    // Which pillar sections are manually expanded. Collapsed by default so the
    // long catalog does not dominate the page.
    const [expanded, setExpanded] = useState({});

    const query = filter.trim().toLowerCase();
    const filtered = query
        ? sources.filter((s) => s.name.toLowerCase().includes(query))
        : sources;

    const grouped = filtered.reduce((groups, source) => {
        const pillar = source.pillar || 'Other';
        (groups[pillar] = groups[pillar] || []).push(source);
        return groups;
    }, {});

    // Selected counts per pillar come from the full list, so the "N selected"
    // badge stays accurate even while a name filter is applied.
    const selectedByPillar = sources.reduce((acc, source) => {
        if (selectedIds.includes(source.id)) {
            const pillar = source.pillar || 'Other';
            acc[pillar] = (acc[pillar] || 0) + 1;
        }
        return acc;
    }, {});

    // Selected sources shown as a recap of removable chips, so picks stay
    // visible (and removable) even when their pillar is collapsed.
    const selectedList = sources.filter((s) => selectedIds.includes(s.id));

    const togglePillar = (key) =>
        setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

    // An active filter force-expands matching pillars so results are visible
    // without extra clicks.
    const isExpanded = (key) => (query ? true : !!expanded[key]);

    const visiblePillars = PILLARS.filter((p) => grouped[p.key]?.length);

    return (
        <Card className="mb-4 shadow-sm">
            <Card.Header
                as="h2"
                className="h5 mb-0 bg-primary text-white d-flex align-items-center gap-2"
            >
                <span className="step-number badge rounded-circle bg-white text-primary">
                    1
                </span>
                Choose data sources
            </Card.Header>
            <Card.Body>
                <p className="text-body-secondary">
                    Select one or more Colorado sustainability data sources to
                    query.
                </p>

                {loading && (
                    <div className="d-flex justify-content-center py-4">
                        <Spinner animation="border" variant="primary" />
                        <span className="visually-hidden">
                            Loading data sources...
                        </span>
                    </div>
                )}

                {error && !loading && (
                    <Alert variant="danger">
                        <Alert.Heading className="h6">
                            Couldn&rsquo;t load data sources
                        </Alert.Heading>
                        <p className="small mb-0">{error}</p>
                    </Alert>
                )}

                {!loading && !error && sources.length > 0 && (
                    <>
                        <Form.Group className="mb-3" controlId="source-filter">
                            <Form.Label className="visually-hidden">
                                Filter data sources by name
                            </Form.Label>
                            <Form.Control
                                type="search"
                                size="sm"
                                placeholder="Filter data sources by name..."
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                            />
                        </Form.Group>

                        {selectedList.length > 0 && (
                            <div className="mb-3 d-flex flex-wrap align-items-center gap-1">
                                <span className="small text-body-secondary me-1">
                                    Selected:
                                </span>
                                {selectedList.map((source) => (
                                    <Button
                                        key={source.id}
                                        variant="primary"
                                        size="sm"
                                        className="rounded-pill d-inline-flex align-items-center gap-1"
                                        onClick={() => onToggle(source.id)}
                                        aria-label={`Remove ${source.name}`}
                                    >
                                        {source.name}
                                        <span aria-hidden="true">&times;</span>
                                    </Button>
                                ))}
                            </div>
                        )}

                        {visiblePillars.map((pillar) => {
                            const list = grouped[pillar.key];
                            const selected = selectedByPillar[pillar.key] || 0;
                            const open = isExpanded(pillar.key);
                            return (
                                <div key={pillar.key} className="mb-2">
                                    <button
                                        type="button"
                                        className="btn btn-light border w-100 d-flex align-items-center justify-content-between"
                                        onClick={() => togglePillar(pillar.key)}
                                        aria-expanded={open}
                                    >
                                        <span className="d-flex align-items-center gap-2">
                                            <Badge
                                                bg={pillar.variant}
                                                text={pillar.text}
                                            >
                                                {pillar.key}
                                            </Badge>
                                            <span className="text-body-secondary small">
                                                {list.length} source
                                                {list.length === 1 ? '' : 's'}
                                            </span>
                                            {selected > 0 && (
                                                <Badge bg="primary" pill>
                                                    {selected} selected
                                                </Badge>
                                            )}
                                        </span>
                                        <span
                                            aria-hidden="true"
                                            className="text-primary fw-bold ms-2"
                                        >
                                            {open ? '▾' : '▸'}
                                        </span>
                                    </button>

                                    {open && (
                                        <div
                                            className="px-2 pt-2"
                                            role="group"
                                            aria-label={`${pillar.key} data sources`}
                                        >
                                            <Row className="g-2">
                                                {list.map((source) => (
                                                    <Col md={6} key={source.id}>
                                                        <Form.Check
                                                            type="checkbox"
                                                            id={`source-${source.id}`}
                                                            checked={selectedIds.includes(
                                                                source.id
                                                            )}
                                                            onChange={() =>
                                                                onToggle(
                                                                    source.id
                                                                )
                                                            }
                                                            label={
                                                                <span>
                                                                    <span className="fw-semibold">
                                                                        {
                                                                            source.name
                                                                        }
                                                                    </span>
                                                                    {source.organization && (
                                                                        <span className="d-block small text-body-secondary">
                                                                            {
                                                                                source.organization
                                                                            }
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            }
                                                        />
                                                    </Col>
                                                ))}
                                            </Row>
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {query && visiblePillars.length === 0 && (
                            <Alert variant="info" className="mb-0">
                                No data sources match &ldquo;{filter.trim()}
                                &rdquo;.
                            </Alert>
                        )}
                    </>
                )}

                {!loading && !error && sources.length === 0 && (
                    <Alert variant="info" className="mb-0">
                        No data sources are available right now. Please try
                        again later.
                    </Alert>
                )}
            </Card.Body>
        </Card>
    );
}

SourceSelector.propTypes = {
    sources: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
                .isRequired,
            name: PropTypes.string.isRequired,
            pillar: PropTypes.string,
            organization: PropTypes.string,
        })
    ).isRequired,
    selectedIds: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    ).isRequired,
    onToggle: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    error: PropTypes.string,
};

// ---------------------------------------------------------------------------
// Step 2 — Search terms
//
// Terms are free text, not column filters: the engine samples each source at
// runtime and exposes no fixed per-source schema, so the user supplies words
// or phrases to look for (joined with AND / OR / NOT) rather than picking a
// field. Each selected source's topic tags are offered as clickable
// suggestions to help pick meaningful terms.
// ---------------------------------------------------------------------------
function SearchTerms({
    terms,
    suggestions,
    onAdd,
    onAddSuggestion,
    onUpdate,
    onRemove,
    disabled,
}) {
    // Hook must run before the early return below.
    const [showAllSuggestions, setShowAllSuggestions] = useState(false);

    if (disabled) {
        return (
            <Alert variant="light" className="mb-0">
                Select at least one data source to add search terms.
            </Alert>
        );
    }

    const shownSuggestions = showAllSuggestions
        ? suggestions
        : suggestions.slice(0, SUGGESTION_CAP);

    return (
        <>
            <p className="text-body-secondary">
                Add words or phrases to search for across your selected sources.
                Combine multiple terms with AND, OR, or NOT.
            </p>

            {suggestions.length > 0 && (
                <div className="mb-3">
                    <span className="small text-body-secondary me-2">
                        Suggested terms:
                    </span>
                    {shownSuggestions.map((suggestion) => (
                        <Button
                            key={suggestion}
                            variant="outline-secondary"
                            size="sm"
                            className="rounded-pill me-1 mb-1 py-0"
                            onClick={() => onAddSuggestion(suggestion)}
                        >
                            + {suggestion}
                        </Button>
                    ))}
                    {suggestions.length > SUGGESTION_CAP && (
                        <Button
                            variant="link"
                            size="sm"
                            className="p-0 align-baseline"
                            onClick={() => setShowAllSuggestions((v) => !v)}
                        >
                            {showAllSuggestions
                                ? 'Show fewer'
                                : `Show all ${suggestions.length}`}
                        </Button>
                    )}
                </div>
            )}

            <div className="mb-3">
                <Button variant="outline-primary" size="sm" onClick={onAdd}>
                    + Add search term
                </Button>
            </div>

            {terms.length === 0 ? (
                <p className="text-body-secondary small mb-0">
                    No search terms yet. Add one to narrow the query, or run
                    with just the selected sources.
                </p>
            ) : (
                <div className="d-flex flex-column gap-2">
                    {terms.map((row, index) => (
                        <Row key={row.id} className="g-2 align-items-end">
                            {index > 0 && (
                                <Col xs={4} md={2}>
                                    <Form.Label
                                        className="small"
                                        htmlFor={`op-${row.id}`}
                                    >
                                        Logic
                                    </Form.Label>
                                    {/* Only on the first operator row, which is
                                        where the choice first appears. */}
                                    {index === 1 && (
                                        <InfoTooltip
                                            text="AND narrows results to items matching both terms, OR widens them to either term,
                                         and NOT excludes a term. Terms combine in order from top to bottom, with no grouping."
                                        />
                                    )}
                                    <Form.Select
                                        id={`op-${row.id}`}
                                        size="sm"
                                        value={row.operator}
                                        onChange={(e) =>
                                            onUpdate(row.id, {
                                                operator: e.target.value,
                                            })
                                        }
                                        aria-label="Logic operator"
                                    >
                                        {OPERATORS.map((op) => (
                                            <option
                                                key={op.value}
                                                value={op.value}
                                            >
                                                {op.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Col>
                            )}

                            <Col
                                xs={index > 0 ? 6 : 10}
                                md={index > 0 ? 9 : 11}
                            >
                                <Form.Label
                                    className="small"
                                    htmlFor={`term-${row.id}`}
                                >
                                    Search term
                                </Form.Label>
                                <Form.Control
                                    id={`term-${row.id}`}
                                    size="sm"
                                    type="text"
                                    placeholder="e.g. wildfire, PM2.5, 2020..."
                                    value={row.text}
                                    onChange={(e) =>
                                        onUpdate(row.id, {
                                            text: e.target.value,
                                        })
                                    }
                                />
                            </Col>

                            <Col xs={2} md={1} className="text-end">
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => onRemove(row.id)}
                                    aria-label={`Remove term ${index + 1}`}
                                >
                                    &times;
                                </Button>
                            </Col>
                        </Row>
                    ))}
                </div>
            )}
        </>
    );
}

SearchTerms.propTypes = {
    terms: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            text: PropTypes.string.isRequired,
            operator: PropTypes.string,
        })
    ).isRequired,
    suggestions: PropTypes.arrayOf(PropTypes.string).isRequired,
    onAdd: PropTypes.func.isRequired,
    onAddSuggestion: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function AdvancedQuery() {
    const navigate = useNavigate();
    const { sources, loading, error } = useDataSources();

    const [selectedIds, setSelectedIds] = useState([]);
    const [terms, setTerms] = useState([]);
    const [timeOption, setTimeOption] = useState('latest');
    const [customRange, setCustomRange] = useState({ start: '', end: '' });
    const [regionId, setRegionId] = useState('statewide');

    // Monotonic id source for term rows (avoids Date.now() collisions when two
    // rows are added in the same millisecond).
    const nextTermId = useRef(1);

    // Topic tags from the selected sources, offered as suggested search terms.
    const suggestedTerms = useMemo(
        () => deriveKeywordFields(sources, selectedIds),
        [sources, selectedIds]
    );

    const selectedSources = useMemo(
        () => sources.filter((s) => selectedIds.includes(s.id)),
        [sources, selectedIds]
    );

    const toggleSource = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const addTerm = () => {
        setTerms((prev) => [
            ...prev,
            {
                id: nextTermId.current++,
                text: '',
                operator: prev.length > 0 ? 'and' : null,
            },
        ]);
    };

    // Add a term pre-filled from a suggested tag, skipping duplicates so
    // clicking the same suggestion twice does not add two identical rows.
    const addSuggestedTerm = (text) => {
        setTerms((prev) => {
            if (prev.some((t) => t.text.toLowerCase() === text.toLowerCase())) {
                return prev;
            }
            return [
                ...prev,
                {
                    id: nextTermId.current++,
                    text,
                    operator: prev.length > 0 ? 'and' : null,
                },
            ];
        });
    };

    const updateTerm = (id, updates) => {
        setTerms((prev) =>
            prev.map((row) => (row.id === id ? { ...row, ...updates } : row))
        );
    };

    const removeTerm = (id) => {
        setTerms((prev) => prev.filter((row) => row.id !== id));
    };

    const handleRun = () => {
        const payload = buildQueryPayload({
            sources: selectedSources,
            terms,
            timeRange: { option: timeOption, ...customRange },
            regionId,
        });
        // Hand the assembled query to a brand-new Expert Query Bili session:
        // navigate to the chat page with the query text in router state, which
        // BiliChat picks up to open an expert-persona conversation and send it.
        navigate('/bili-chat', {
            state: { expertQuery: describeQueryPayload(payload) },
        });
    };

    const canRun = selectedIds.length > 0;

    return (
        <>
            <SEO
                title="Advanced Query"
                description="Build a precise query against Colorado sustainability data sources."
                breadcrumbs={[
                    { name: 'Home', path: '/' },
                    { name: 'Advanced Query', path: '/advanced-query' },
                ]}
            />
            <PreviewBanner
                title="Advanced Query Preview"
                message="Build a query and run it to explore the results with Bili. Map view and data export are coming later."
                icon="🔍"
                variant="warning"
                showOverlay={false}
            />
            <section id="advanced-query" className="page-container bg-light">
                <Container className="py-4 aq-content">
                    <Row className="mb-4">
                        <Col>
                            <h1 className="text-primary fw-bold mb-2">
                                Advanced Query
                            </h1>
                            <p className="text-body-secondary mb-0">
                                Search Colorado sustainability data directly:
                                pick your data sources, add search terms, set a
                                time range and geographic area, then run the
                                query.
                            </p>
                        </Col>
                    </Row>

                    <SourceSelector
                        sources={sources}
                        selectedIds={selectedIds}
                        onToggle={toggleSource}
                        loading={loading}
                        error={error}
                    />

                    <Card className="mb-4 shadow-sm">
                        <Card.Header
                            as="h2"
                            className="h5 mb-0 bg-primary text-white d-flex align-items-center gap-2"
                        >
                            <span className="step-number badge rounded-circle bg-white text-primary">
                                2
                            </span>
                            Add search terms
                        </Card.Header>
                        <Card.Body>
                            <SearchTerms
                                terms={terms}
                                suggestions={suggestedTerms}
                                onAdd={addTerm}
                                onAddSuggestion={addSuggestedTerm}
                                onUpdate={updateTerm}
                                onRemove={removeTerm}
                                disabled={selectedIds.length === 0}
                            />
                        </Card.Body>
                    </Card>

                    <Row className="g-4 mb-4">
                        <Col md={6}>
                            <Card className="h-100 shadow-sm">
                                <Card.Header
                                    as="h2"
                                    className="h5 mb-0 bg-primary text-white d-flex align-items-center gap-2"
                                >
                                    <span className="step-number badge rounded-circle bg-white text-primary">
                                        3
                                    </span>
                                    Time range
                                </Card.Header>
                                <Card.Body>
                                    <Form.Group>
                                        <Form.Label htmlFor="time-option">
                                            Time period
                                        </Form.Label>
                                        <Form.Select
                                            id="time-option"
                                            size="sm"
                                            value={timeOption}
                                            onChange={(e) =>
                                                setTimeOption(e.target.value)
                                            }
                                        >
                                            {TIME_OPTIONS.map((opt) => (
                                                <option
                                                    key={opt.value}
                                                    value={opt.value}
                                                >
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Text className="text-body-secondary">
                                            {timeOption === 'latest'
                                                ? 'Pulls the most recent data available from each source.'
                                                : 'Limit results to a specific date range.'}
                                        </Form.Text>
                                    </Form.Group>

                                    {timeOption === 'custom' && (
                                        <Row className="g-2 mt-1">
                                            <Col xs={6}>
                                                <Form.Label
                                                    className="small"
                                                    htmlFor="date-start"
                                                >
                                                    Start
                                                </Form.Label>
                                                <Form.Control
                                                    id="date-start"
                                                    type="date"
                                                    size="sm"
                                                    value={customRange.start}
                                                    onChange={(e) =>
                                                        setCustomRange(
                                                            (prev) => ({
                                                                ...prev,
                                                                start: e.target
                                                                    .value,
                                                            })
                                                        )
                                                    }
                                                />
                                            </Col>
                                            <Col xs={6}>
                                                <Form.Label
                                                    className="small"
                                                    htmlFor="date-end"
                                                >
                                                    End
                                                </Form.Label>
                                                <Form.Control
                                                    id="date-end"
                                                    type="date"
                                                    size="sm"
                                                    value={customRange.end}
                                                    onChange={(e) =>
                                                        setCustomRange(
                                                            (prev) => ({
                                                                ...prev,
                                                                end: e.target
                                                                    .value,
                                                            })
                                                        )
                                                    }
                                                />
                                            </Col>
                                        </Row>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={6}>
                            <Card className="h-100 shadow-sm">
                                <Card.Header
                                    as="h2"
                                    className="h5 mb-0 bg-primary text-white d-flex align-items-center gap-2"
                                >
                                    <span className="step-number badge rounded-circle bg-white text-primary">
                                        4
                                    </span>
                                    Geographic area
                                </Card.Header>
                                <Card.Body>
                                    <Form.Group>
                                        <Form.Label htmlFor="region-select">
                                            Colorado region
                                        </Form.Label>
                                        <Form.Select
                                            id="region-select"
                                            size="sm"
                                            value={regionId}
                                            onChange={(e) =>
                                                setRegionId(e.target.value)
                                            }
                                        >
                                            {COLORADO_REGIONS.map((region) => (
                                                <option
                                                    key={region.id}
                                                    value={region.id}
                                                >
                                                    {region.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Text className="text-body-secondary">
                                            {
                                                COLORADO_REGIONS.find(
                                                    (r) => r.id === regionId
                                                )?.description
                                            }
                                        </Form.Text>
                                    </Form.Group>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    <Card className="shadow-sm">
                        <Card.Body>
                            <Row className="align-items-center">
                                <Col md={8}>
                                    <h2 className="h5 mb-1">Run your query</h2>
                                    <p className="text-body-secondary mb-0 small">
                                        {selectedIds.length} data source
                                        {selectedIds.length === 1
                                            ? ''
                                            : 's'}{' '}
                                        selected,{' '}
                                        {
                                            terms.filter((t) => t.text.trim())
                                                .length
                                        }{' '}
                                        search terms.
                                    </p>
                                </Col>
                                <Col
                                    md={4}
                                    className="text-md-end mt-2 mt-md-0"
                                >
                                    <Button
                                        variant="primary"
                                        size="lg"
                                        onClick={handleRun}
                                        disabled={!canRun}
                                    >
                                        Run query
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Container>
            </section>
        </>
    );
}
